import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, CheckCircle2, Clock, AlertCircle, ClipboardCheck, Building2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { usePublicRoomDisplay } from '@/hooks/usePublicRoomDisplay'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { DualTimeDisplay } from '@/components/DualTimeDisplay'

/** Default branding when org has none configured */
const DEFAULT_PRIMARY_COLOR = '#0f172a' // Tailwind slate-900

/**
 * Public room display page for QR code targets.
 * Shows room cleaning status with org branding.
 * No authentication required.
 */
export function PublicRoomDisplayPage() {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { data, isLoading, error } = usePublicRoomDisplay(roomId ?? '')

  // Dynamic branding styles
  const primaryColor = data?.branding?.primary_color || DEFAULT_PRIMARY_COLOR
  const orgName = data?.branding?.org_name || 'Tidy Tag'
  const logoUrl = data?.branding?.logo_url

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: primaryColor }}
      >
        <Loader2 className="h-12 w-12 animate-spin text-white" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
        <AlertCircle className="h-16 w-16 mb-4 opacity-50" />
        <h1 className="text-2xl font-bold mb-2">Room Not Found</h1>
        <p className="text-white/70 text-center">
          This room display is not available. Please check the QR code or contact
          building management.
        </p>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: primaryColor }}
    >
      {/* Header with branding */}
      <header className="p-6 text-white">
        <div className="flex items-center justify-center gap-3">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={`${orgName} logo`}
              className="h-12 w-auto object-contain"
            />
          )}
          <div className="text-2xl font-bold">{orgName}</div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-2xl">
          <CardContent className="p-8">
            {/* Room name */}
            <h1 className="text-3xl font-bold text-center mb-2">
              {data.room_name}
            </h1>

            {/* Building name */}
            {data.building_name && (
              <div className="flex items-center justify-center gap-1.5 text-muted-foreground mb-8">
                <Building2 className="h-4 w-4" />
                <span className="text-sm">{data.building_name}</span>
              </div>
            )}
            {!data.building_name && <div className="mb-6" />}

            {/* Last cleaning info */}
            {data.last_cleaning ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                    <CheckCircle2 className="h-8 w-8" />
                    <span className="text-lg font-semibold">Last Cleaned</span>
                  </div>
                  <div className="text-lg font-semibold flex justify-center">
                    <DualTimeDisplay
                      utcTimestamp={data.last_cleaning.timestamp}
                      orgTimezone={data.branding?.timezone}
                      fallbackDate={data.last_cleaning.date}
                      fallbackTime={data.last_cleaning.time}
                      className="text-center"
                    />
                  </div>
                </div>

                {/* Previous cleaning */}
                {data.previous_cleaning && (
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-center gap-2 text-muted-foreground mb-2">
                      <Clock className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        Previous Cleaning
                      </span>
                    </div>
                    <div className="flex justify-center text-muted-foreground">
                      <DualTimeDisplay
                        utcTimestamp={data.previous_cleaning.timestamp}
                        orgTimezone={data.branding?.timezone}
                        fallbackDate={data.previous_cleaning.date}
                        fallbackTime={data.previous_cleaning.time}
                        className="text-center"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No recent cleaning records</p>
                <p className="text-sm mt-2">
                  Cleaning data will appear here once recorded.
                </p>
              </div>
            )}

            {/* Create Report button for authenticated users */}
            {isAuthenticated && (
              <div className="mt-8 border-t pt-6">
                <Button
                  className="w-full"
                  size="lg"
                  onClick={() => navigate('/reports/new', { state: { roomId } })}
                >
                  <ClipboardCheck className="mr-2 h-5 w-5" />
                  Create Cleaning Report
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Compliance note */}
        <p className="text-white/70 text-xs mt-8 text-center max-w-sm">
          This display satisfies Ontario Regulation 480/24 washroom cleaning
          compliance requirements.
        </p>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-white/50 text-xs">
        Powered by {orgName === 'Tidy Tag' ? 'Tidy Tag' : `Tidy Tag for ${orgName}`}
      </footer>
    </div>
  )
}
